import { useState } from "react";
import { useNavigate } from "react-router";
import { useAppState, useGoLive } from "@/lib/queries";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  DialogRoot,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog";
import { useToast } from "@/components/ui/Toast";

interface AppShape {
  account?: {
    isSandbox?: boolean;
    companyName?: string;
    twilio?: { phoneNumber?: string };
  };
  platform?: {
    twilioConfig?: { configured?: boolean; number?: string };
    emailConfig?: { configured?: boolean; from?: string };
  };
}

/**
 * Persistent sandbox banner + go-live confirmation.
 *
 * The banner stays simple (badge + "Go live" button). The confirmation
 * is a proper modal dialog (role=dialog, aria-modal, focus trap) — the
 * audit walkthrough graded the inline-confirm-in-status-region pattern
 * as the weakest moment-of-truth in the product because (a) focus
 * wasn't trapped, (b) it listed only what gets deleted with no "what
 * survives" framing, (c) no billing language, (d) no precondition check
 * on Twilio / Email being configured.
 *
 * Three-block body: What survives, What changes, Billing impact.
 * Precondition gate: disables "Yes, go live" until Twilio + Email
 * are connected, and names what's missing inline.
 */
export function SandboxBanner() {
  const { data: appRaw } = useAppState();
  const goLive = useGoLive();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const app = (appRaw ?? {}) as AppShape;
  if (!app.account?.isSandbox) return null;

  // Prereq detection: twilio is considered configured if the platform
  // config flag is set OR the account-level twilio.phoneNumber is on
  // file (mirrors OnboardingWizard.shouldShow's signal). Email is
  // considered configured when the platform-level from address is set.
  const twilioReady = Boolean(
    app.platform?.twilioConfig?.configured ||
      app.account?.twilio?.phoneNumber?.trim() ||
      app.platform?.twilioConfig?.number?.trim(),
  );
  const emailReady = Boolean(
    app.platform?.emailConfig?.configured ||
      app.platform?.emailConfig?.from?.trim(),
  );
  const missing: string[] = [];
  if (!twilioReady) missing.push("Twilio");
  if (!emailReady) missing.push("Email");
  const ready = missing.length === 0;

  const handleConfirm = () => {
    goLive.mutate(undefined, {
      onSuccess: () => {
        setOpen(false);
        toast({
          title: "You're live",
          description:
            "Sandbox data has been cleared. Connect your data to start working real customer activity.",
          tone: "success",
        });
      },
      onError: (err) => {
        toast({
          title: "Couldn't go live",
          description: (err as Error).message || "Try again in a moment.",
          tone: "error",
        });
      },
    });
  };

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        className="sticky top-0 z-40 w-full bg-tone-sun-soft border-b border-tone-sun/40 px-dw-4 py-dw-2 flex flex-wrap items-center gap-dw-3"
      >
        <Badge tone="sun">SANDBOX</Badge>
        <span className="hidden sm:inline text-sm text-tone-sun font-medium">
          Demo data — explore freely. Going live wipes this and starts you on
          real activity.
        </span>
        <div className="ml-auto flex items-center gap-dw-2">
          <Button
            size="sm"
            onClick={() => setOpen(true)}
            disabled={goLive.isPending}
          >
            Go live
          </Button>
        </div>
      </div>

      <DialogRoot
        open={open}
        onOpenChange={(next) => {
          if (!goLive.isPending) setOpen(next);
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogTitle>Ready to go live?</DialogTitle>
          <DialogDescription>
            Flipping out of sandbox is permanent. Here's exactly what changes
            and what stays the same.
          </DialogDescription>

          <div className="mt-dw-4 space-y-dw-4 text-sm">
            {/* What survives */}
            <section>
              <h3 className="font-display text-dw-text mb-dw-2">
                What survives
              </h3>
              <ul className="list-disc list-inside space-y-1 text-dw-text-2">
                <li>Your account, members, and roles</li>
                <li>Twilio number and messaging configuration</li>
                <li>Email sending configuration</li>
                <li>Response SLAs and cadence settings</li>
                <li>Audit trail (start fresh history continues forward)</li>
              </ul>
            </section>

            {/* What changes */}
            <section>
              <h3 className="font-display text-dw-text mb-dw-2">What changes</h3>
              <ul className="list-disc list-inside space-y-1 text-dw-text-2">
                <li>All sandbox demo records are permanently deleted</li>
                <li>
                  Real customer outreach (SMS / email / calls) starts firing as
                  inbound activity arrives
                </li>
                <li>"Reset to sandbox seed" is no longer available</li>
              </ul>
            </section>

            {/* Billing impact */}
            <section>
              <h3 className="font-display text-dw-text mb-dw-2">
                Billing impact
              </h3>
              <p className="text-dw-text-2">
                Going live activates billing under your Recovery Console plan.{" "}
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate("/billing");
                  }}
                  className="text-dw-blue hover:underline"
                >
                  Review plan & pricing on /billing
                </button>{" "}
                before continuing.
              </p>
            </section>

            {/* Prereq gate */}
            {!ready && (
              <section
                role="alert"
                className="rounded-dw-md border border-dw-red bg-dw-red/10 px-dw-3 py-dw-2 text-dw-text"
              >
                <p className="font-medium">Not ready yet — set up first:</p>
                <ul className="list-disc list-inside mt-1 text-dw-text-2">
                  {missing.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
                <p className="text-xs text-dw-text-3 mt-1">
                  Without these the product can't actually reach customers
                  after you flip — finish them on{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      navigate("/platform/setup");
                    }}
                    className="text-dw-blue hover:underline"
                  >
                    /platform/setup
                  </button>
                  .
                </p>
              </section>
            )}
          </div>

          <div className="mt-dw-5 flex items-center justify-end gap-dw-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={goLive.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirm}
              disabled={goLive.isPending || !ready}
              title={
                ready
                  ? undefined
                  : `Configure ${missing.join(" and ")} before going live`
              }
            >
              {goLive.isPending ? "Going live…" : "Yes, go live"}
            </Button>
          </div>
        </DialogContent>
      </DialogRoot>
    </>
  );
}
